/**
 * Brokerage section — broker (agent) picker by email.
 *
 * Source of truth: the BROKER (a person — formerly "agent" on the v1
 * API). The selector lists brokers from `/v2/brokers/`, identified by
 * email; selecting one fills the rest of the section:
 *
 *   • brokerage.brokerId        ← agent.id        (= submission.broker_id; matches FK)
 *   • brokerage.brokerName      ← agent.name
 *   • brokerage.brokerEmail     ← agent.email     (mirrors the selector value)
 *   • brokerage.brokerageName   ← agent.brokerage.name
 *   • brokerage.brokerageAddress← formatted from agent.brokerage.{address,city,state,zip}
 *
 * Layout: 2×2 grid — Broker Email + Broker Name on row 1, Brokerage
 * Address + Brokerage Name on row 2. None of the four are required —
 * `broker_id` is nullable on the v2 backend, so saving without
 * picking a brokerage is fine. The three "Automatically Populated"
 * inputs are read-only views of the selected agent and never
 * validated as user input.
 *
 * "Add New" opens the AgentModal which creates an agent under a
 * chosen brokerage and then auto-selects it here.
 */
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Input, SearchSelect } from "@tricura/atlas-ui";
import { formatAgentBrokerageAddress, getAgent, listAgents } from "../../../../api";
import { AgentModal } from "../../../../components/agent-modal";
import { FormGroup } from "../../../../components/form-group";
import { useDebouncedValue } from "../../../../hooks/use-debounced-value";
import type { AgentRow } from "../../../../types/submission";
import type { UwInformationFormData } from "../schema";

const AUTOFILL_PLACEHOLDER = "Automatically Populated";

/**
 * Visual style for the three read-only auto-filled inputs (Broker
 * Name, Brokerage Address, Brokerage Name). The hint pales the text,
 * placeholder and trailing help icon to the same light gray
 * (text-quaternary = #737373) so the user reads the field as
 * "managed by the system" — even after the autofill writes a real
 * value. Border keeps the default border-primary (#D4D4D4) ring.
 */
const READONLY_INPUT_CLASS = "text-quaternary placeholder:text-quaternary";
const READONLY_TOOLTIP_CLASS = "text-fg-quaternary";

type SetValue = (name: string, value: string, options?: { shouldDirty?: boolean }) => void;

// applyAgent only runs on user actions (picking/clearing the broker email, or
// adding a broker) — not on load hydration — so it marks the form dirty. Without
// shouldDirty the Save button never enabled after changing the broker email.
function applyAgent(setValue: SetValue, agent: AgentRow | null) {
    const dirty = { shouldDirty: true };
    if (!agent) {
        setValue("brokerage.brokerId", "", dirty);
        setValue("brokerage.brokerName", "", dirty);
        setValue("brokerage.brokerEmail", "", dirty);
        setValue("brokerage.brokerageName", "", dirty);
        setValue("brokerage.brokerageAddress", "", dirty);
        return;
    }
    setValue("brokerage.brokerId", agent.id, dirty);
    setValue("brokerage.brokerName", agent.name, dirty);
    setValue("brokerage.brokerEmail", agent.email ?? "", dirty);
    setValue("brokerage.brokerageName", agent.brokerage?.name ?? "", dirty);
    setValue("brokerage.brokerageAddress", formatAgentBrokerageAddress(agent), dirty);
}

export function BrokerageSection() {
    const { control, setValue, watch } = useFormContext<UwInformationFormData>();
    // `results` = latest server page for the current query; `selectedAgent`
    // is tracked separately so the trigger keeps showing the chosen email
    // even after a later search returns a list that doesn't include it.
    const [results, setResults] = useState<AgentRow[]>([]);
    const [selectedAgent, setSelectedAgent] = useState<AgentRow | null>(null);
    const [query, setQuery] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

    const currentBrokerId = watch("brokerage.brokerId");
    const debouncedQuery = useDebouncedValue(query.trim(), 300);

    // Server-side search-as-you-type: re-query the backend whenever the
    // debounced term changes (empty term = first page). Previous results
    // stay until the new ones resolve, so the list doesn't flash empty.
    useEffect(() => {
        let cancelled = false;
        listAgents({ search: debouncedQuery || undefined, limit: 50 })
            .then((data) => {
                if (!cancelled) setResults(data);
            })
            .catch((error) => {
                console.error("Failed to load agents", error);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedQuery]);

    // Preselect the saved broker_id: fetch it on its own so the selector
    // shows the email even if it's not in the current search page.
    useEffect(() => {
        if (!currentBrokerId) {
            setSelectedAgent(null);
            return;
        }
        if (selectedAgent?.id === currentBrokerId) return;
        let cancelled = false;
        getAgent(currentBrokerId)
            .then((agent) => {
                if (!cancelled && agent) setSelectedAgent(agent);
            })
            .catch(() => {
                /* ignore — selector just stays empty for unknown ids */
            });
        return () => {
            cancelled = true;
        };
    }, [currentBrokerId, selectedAgent]);

    // Options = server results, with the selected agent kept at the top so
    // its label renders in the trigger regardless of the current query.
    const optionAgents =
        selectedAgent && !results.some((a) => a.id === selectedAgent.id) ? [selectedAgent, ...results] : results;
    const items = optionAgents.map((a) => ({
        id: a.id,
        // Email is the primary identifier the user picks by; name +
        // brokerage go in supportingText so they can disambiguate two
        // people sharing an email-less profile.
        label: a.email || a.name || "—",
        supportingText: a.brokerage?.name ? `${a.name} · ${a.brokerage.name}` : a.name || "—",
    }));

    const brokerName = watch("brokerage.brokerName");
    const brokerageName = watch("brokerage.brokerageName");
    const brokerageAddress = watch("brokerage.brokerageAddress");

    return (
        <>
            <FormGroup title="Brokerage">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <Controller
                        control={control}
                        name="brokerage.brokerId"
                        render={({ field }) => (
                            <SearchSelect
                                size="sm"
                                label="Broker Email"
                                placeholder="Select Broker Email"
                                items={items}
                                selectedKey={field.value || null}
                                onSearchChange={setQuery}
                                onSelectionChange={(k) => {
                                    const id = (k as string) ?? "";
                                    if (!id) {
                                        setSelectedAgent(null);
                                        applyAgent(setValue as SetValue, null);
                                        return;
                                    }
                                    const agent = optionAgents.find((a) => a.id === id) ?? null;
                                    setSelectedAgent(agent);
                                    applyAgent(setValue as SetValue, agent);
                                }}
                                actionLabel="Add New"
                                onAction={() => setIsModalOpen(true)}
                                actionDataAutotest="uw-information-brokerage-broker-email-add-new-button"
                                searchDataAutotest="uw-information-brokerage-broker-email-search-input"
                                data-autotest="uw-information-brokerage-broker-email-select"
                            >
                                {(item) => (
                                    <SearchSelect.Item
                                        id={item.id}
                                        supportingText={item.supportingText}
                                        data-autotest="uw-information-brokerage-broker-email-option"
                                    >
                                        {item.label}
                                    </SearchSelect.Item>
                                )}
                            </SearchSelect>
                        )}
                    />

                    <Input
                        size="sm"
                        label="Broker Name"
                        placeholder={AUTOFILL_PLACEHOLDER}
                        isReadOnly
                        tooltip="The Broker name will be automatically filled after selecting a Broker email"
                        inputClassName={READONLY_INPUT_CLASS}
                        tooltipClassName={READONLY_TOOLTIP_CLASS}
                        value={brokerName}
                        data-autotest="uw-information-brokerage-broker-name-input"
                    />

                    <Input
                        size="sm"
                        label="Brokerage Address"
                        placeholder={AUTOFILL_PLACEHOLDER}
                        isReadOnly
                        tooltip="The Brokerage Address will be automatically filled after selecting a Broker email"
                        inputClassName={READONLY_INPUT_CLASS}
                        tooltipClassName={READONLY_TOOLTIP_CLASS}
                        value={brokerageAddress}
                        data-autotest="uw-information-brokerage-brokerage-address-input"
                    />

                    <Input
                        size="sm"
                        label="Brokerage Name"
                        placeholder={AUTOFILL_PLACEHOLDER}
                        isReadOnly
                        tooltip="The Brokerage name will be automatically filled after selecting a Broker email"
                        inputClassName={READONLY_INPUT_CLASS}
                        tooltipClassName={READONLY_TOOLTIP_CLASS}
                        value={brokerageName}
                        data-autotest="uw-information-brokerage-brokerage-name-input"
                    />
                </div>
            </FormGroup>

            <AgentModal
                isOpen={isModalOpen}
                onOpenChange={setIsModalOpen}
                onCreated={(agent) => {
                    setSelectedAgent(agent);
                    applyAgent(setValue as SetValue, agent);
                }}
            />
        </>
    );
}
